import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, MailCheck, RotateCcw, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

type SignupStep = 'create' | 'confirm';

export function SignupPage() {
  const navigate = useNavigate();
  const {
    signup,
    confirmSignup,
    resendSignupCode,
    login,
    googleLogin,
    linkedinLogin,
    initializeSession,
    isAuthenticated,
    user,
    isLoading,
    error,
    setError,
  } = useAuthStore();

  const [step, setStep] = useState<SignupStep>('create');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [notice, setNotice] = useState('');

  const socialLoginEnabled = import.meta.env.VITE_AUTH_GOOGLE_ENABLED === 'true' || import.meta.env.VITE_AUTH_LINKEDIN_ENABLED === 'true';
  const googleLoginEnabled = import.meta.env.VITE_AUTH_GOOGLE_ENABLED === 'true';
  const linkedinLoginEnabled = import.meta.env.VITE_AUTH_LINKEDIN_ENABLED === 'true';

  useEffect(() => {
    if (isAuthenticated) {
      if (user && !user.onboardingCompleted) {
        navigate('/onboarding', { replace: true });
        return;
      }
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, user]);

  const handleCreateAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice('');
    setError(null);

    try {
      await initializeSession();
      const currentAuth = useAuthStore.getState();
      if (currentAuth.isAuthenticated) {
        const destination = currentAuth.user?.onboardingCompleted === false ? '/onboarding' : '/dashboard';
        navigate(destination, { replace: true });
        return;
      }

      const result = await signup(email.trim(), password, fullName.trim());
      if (result.requiresConfirmation) {
        setStep('confirm');
        setNotice('We sent a confirmation code to your email.');
        return;
      }

      await login(email.trim(), password);
      navigate('/dashboard', { replace: true });
    } catch (_error) {
      // handled by store
    }
  };

  const handleConfirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice('');
    setError(null);

    try {
      await confirmSignup(email.trim(), confirmationCode.trim());
      await login(email.trim(), password);
      navigate('/dashboard', { replace: true });
    } catch (_error) {
      // handled by store
    }
  };

  const handleResend = async () => {
    setNotice('');
    setError(null);
    try {
      await resendSignupCode(email.trim());
      setNotice('A new confirmation code has been sent.');
    } catch (_error) {
      // handled by store
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-foreground">Create your account</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:text-primary/90">
              Sign in
            </Link>
          </p>
        </div>

        <div className="bg-card p-8 rounded-lg border shadow-sm">
          {step === 'create' && socialLoginEnabled ? (
            <div className="mb-6 space-y-4">
              {googleLoginEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 justify-center text-base"
                  onClick={() => googleLogin()}
                  disabled={isLoading}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>
              ) : null}

              {linkedinLoginEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 justify-center text-base"
                  onClick={() => linkedinLogin()}
                  disabled={isLoading}
                >
                  <Linkedin className="h-5 w-5 mr-2 text-[#0A66C2]" />
                  Continue with LinkedIn
                </Button>
              ) : null}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-card px-2 text-muted-foreground">Or create an account with email</span>
                </div>
              </div>
            </div>
          ) : null}

          {step === 'create' ? (
            <form className="space-y-6" onSubmit={handleCreateAccount}>
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-foreground">
                  Full name
                </label>
                <div className="mt-1">
                  <Input
                    id="fullName"
                    name="fullName"
                    autoComplete="name"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email address
                </label>
                <div className="mt-1">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <div className="mt-1">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {error ? <div className="text-sm text-destructive font-medium">{error}</div> : null}

              <div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Create account
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleConfirm}>
              <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground flex items-center gap-2">
                <MailCheck className="h-4 w-4" />
                Enter the code sent to {email}
              </div>

              <div>
                <label htmlFor="confirmationCode" className="block text-sm font-medium text-foreground">
                  Confirmation code
                </label>
                <div className="mt-1">
                  <Input
                    id="confirmationCode"
                    name="confirmationCode"
                    required
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {notice ? <div className="text-sm text-muted-foreground">{notice}</div> : null}
              {error ? <div className="text-sm text-destructive font-medium">{error}</div> : null}

              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Confirm and continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => void handleResend()}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Resend code
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
